// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import { formatDateTime } from '@/lib/dateUtils';
import { useState } from 'react';
import { type Transaction } from '@/types/transaction';
import { useAccounts } from '@/store/accountsStore';
import { useAuthStore } from '@/store/authStore';
import { useTransactionStore } from '@/store/transactionsStore';
import { useSettingsStore } from '@flows.cash/core';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Pencil, Trash2, Download, ArrowDownRight, ArrowUpRight, ExternalLink, Receipt, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatters';
import { useCategoriesStore } from '@/store/categoriesStore';
import { useTagsStore } from '@/store/tagsStore';
import { useCurrenciesStore } from '@/store/currenciesStore';
import EditTransaction from './EditTransaction';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from 'react-i18next';

// Define Timestamp interface for compatibility
interface Timestamp {
  toDate(): Date;
}

interface TransactionDetailsModalProps {
  transaction: Transaction;
  isOpen: boolean;
  onClose: () => void;
}

// Simple TransactionActions component
const TransactionActions = ({ transactionId, onEdit, onClose }: { transactionId: string, onEdit: () => void, onClose: () => void }) => {
  const { deleteTransaction } = useTransactionStore();
  
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      await deleteTransaction(transactionId);
      // Close the modal after deletion
      onClose();
    }
  };
  
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="icon" onClick={onEdit} title="Edit">
        <Pencil className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="icon" onClick={handleDelete} title="Delete">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Simple AttachmentList component
const AttachmentList = ({ 
  attachments
}: { 
  attachments: any[]
}) => {
  if (!attachments || attachments.length === 0) {
    return <p className="text-sm text-gray-500">No attachments</p>;
  }
  
  return (
    <div className="space-y-2">
      {attachments.map((attachment, index) => (
        <div key={index} className="flex items-center gap-2">
          <Receipt className="h-4 w-4" />
          <span>{attachment.name || `Attachment ${index + 1}`}</span>
        </div>
      ))}
    </div>
  );
};

export default function TransactionDetailsModal({ 
  transaction, 
  isOpen, 
  onClose 
}: TransactionDetailsModalProps) {
  const { accounts } = useAccounts();
  const { tags } = useTagsStore();
  const { categories } = useCategoriesStore();
  const { currencies } = useCurrenciesStore();
  const { user } = useAuthStore();
  const { getFilteredTransactions } = useTransactionStore();
  const { settings } = useSettingsStore();
  const [showEditModal, setShowEditModal] = useState(false);
  const { t } = useTranslation();
  
  if (!transaction || !user) return null;

  const handleEdit = () => {
    onClose();  // Close details modal
    setShowEditModal(true);  // Show edit modal
  };

  const account = accounts.find((a: any) => a.id === transaction.accountId);
  const currencyCode = account?.currency || 'USD';
  // Find the currency object from the currencies array
  const currency = currencies.find(c => c.code === currencyCode);

  const getCategoryInfo = () => {
    if (!transaction.categoryId) return { name: 'Uncategorized', color: '#A3A3A3', isDefault: true };
    
    let mainCatId, subCatId;
    if (transaction.categoryId.includes(':')) {
      [mainCatId, subCatId] = transaction.categoryId.split(':');
    } else if (transaction.categoryId.includes('/')) {
      [mainCatId, subCatId] = transaction.categoryId.split('/');
    } else {
      subCatId = transaction.categoryId;
    }

    if (!subCatId) return { name: 'Uncategorized', color: '#A3A3A3', isDefault: true };

    for (const mainCategory of categories) {
      const subcategory = mainCategory.subcategories.find(sub => sub.id === subCatId);
      if (subcategory) {
        return { 
          name: subcategory.name, 
          color: subcategory.color || mainCategory.color, 
          isDefault: false 
        };
      }
    }

    if (mainCatId) {
      const mainCategory = categories.find(cat => cat.id === mainCatId);
      if (mainCategory) {
        const subcategory = mainCategory.subcategories.find(sub => sub.id === subCatId);
        if (subcategory) {
          return { 
            name: subcategory.name, 
            color: subcategory.color || mainCategory.color, 
            isDefault: false 
          };
        }
      }
    }

    return { name: transaction.category || 'Uncategorized', color: '#A3A3A3', isDefault: true };
  };

  const categoryInfo = getCategoryInfo();

  const formatAmount = (amount: number) => {
    // Use the user's preferred number format from settings
    const numberFormat = settings?.number_format || '#,##0.00';
    
    return formatCurrency(amount, currency, {
      showSymbol: true,
      showCode: true,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      numberFormat: numberFormat
    });
  };

  const formatDate = (date: Date | Timestamp | string | number) => {
    try {
      if (!date) return '';
      
      // Handle different date types
      let dateToFormat: Date;
      
      if (typeof date === 'object' && date !== null && 'toDate' in date) {
        // Handle Timestamp-like objects
        dateToFormat = (date as Timestamp).toDate();
      } else if (date instanceof Date) {
        dateToFormat = date;
      } else {
        // Handle string or number
        dateToFormat = new Date(date);
      }
      
      return formatDateTime(dateToFormat, {
        dateFormat: settings?.date_format,
        timeFormat: settings?.time_format
      });
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Invalid date';
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-xl font-semibold">
              {transaction.description || t('transactions.noDescription')}
            </DialogTitle>
            <TransactionActions 
              transactionId={transaction.id} 
              onEdit={handleEdit}
              onClose={onClose}
            />
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Transaction Type and Amount */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center",
                    transaction.type === 'expense' 
                      ? 'bg-[#E53935]/10 text-[#E53935]' 
                      : 'bg-[#4CAF50]/10 text-[#4CAF50]'
                  )}
                >
                  {transaction.type === 'expense' ? (
                    <ArrowDownRight className="w-6 h-6" />
                  ) : (
                    <ArrowUpRight className="w-6 h-6" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-neutral-600 dark:text-[#A3A3A3] capitalize">
                    {transaction.type}
                  </p>
                  <h3 className="text-2xl font-bold">
                    <span 
                      className={cn(
                        transaction.type === 'expense' 
                          ? 'text-[#E53935]' 
                          : 'text-[#4CAF50]'
                      )}
                    >
                      {transaction.type === 'expense' ? '-' : '+'}
                      {formatAmount(transaction.amount)}
                    </span>
                  </h3>
                </div>
              </div>
            </div>

            {/* Description (only show if different from title) */}
            {transaction.description && transaction.description !== transaction.title && (
              <div>
                <h4 className="text-sm font-medium text-neutral-600 dark:text-[#A3A3A3] mb-1">
                  Description
                </h4>
                <p className="text-lg font-medium text-neutral-900 dark:text-[#E8E8E8]">
                  {transaction.description}
                </p>
              </div>
            )}

            {/* Date and Account */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-neutral-600 dark:text-[#A3A3A3] mb-1">
                  Date
                </h4>
                <p className="text-base font-medium text-neutral-900 dark:text-[#E8E8E8]">
                  {formatDate(transaction.date)}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-neutral-600 dark:text-[#A3A3A3] mb-1">
                  Account
                </h4>
                <Badge 
                  variant="secondary"
                  className="bg-neutral-100 dark:bg-[#0E131E] text-neutral-700 dark:text-[#E8E8E8] border-neutral-200 dark:border-[#333840] font-medium"
                >
                  {account?.name || 'Unknown Account'}
                </Badge>
              </div>
            </div>

            {/* Category */}
            <div>
              <h4 className="text-sm font-medium text-neutral-600 dark:text-[#A3A3A3] mb-1">
                Category
              </h4>
              <Badge 
                variant="outline"
                className={cn(
                  "font-medium text-base",
                  categoryInfo.isDefault && "text-gray-400 dark:text-gray-500 italic"
                )}
                style={{ 
                  backgroundColor: `${categoryInfo.color}10`,
                  color: categoryInfo.isDefault ? undefined : categoryInfo.color,
                  borderColor: `${categoryInfo.color}30`
                }}
              >
                {categoryInfo.name}
              </Badge>
            </div>

            {/* Tags */}
            {transaction.tags && transaction.tags.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-neutral-600 dark:text-[#A3A3A3] mb-1">
                  Tags
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {transaction.tags.map((tag) => {
                    const tagData = typeof tag === 'string' 
                      ? tags.find(t => t.id === tag)
                      : tag;

                    if (!tagData) return null;
                    
                    return (
                      <Badge
                        key={tagData.id}
                        variant="outline"
                        className="text-sm font-medium"
                        style={{ 
                          backgroundColor: `${tagData.color}10`,
                          color: tagData.color,
                          borderColor: `${tagData.color}30`
                        }}
                      >
                        {tagData.name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            {transaction.notes && (
              <div>
                <h4 className="text-sm font-medium text-neutral-600 dark:text-[#A3A3A3] mb-1">
                  Notes
                </h4>
                <div className="text-base text-neutral-900 dark:text-[#E8E8E8] whitespace-pre-wrap bg-neutral-50 dark:bg-[#0E131E] rounded-lg p-3 font-medium border border-neutral-200 dark:border-[#333840]">
                  {transaction.notes}
                </div>
              </div>
            )}

            {/* Attachments */}
            {transaction.attachments && transaction.attachments.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-neutral-600 dark:text-[#A3A3A3] mb-2">
                  Attachments
                </h4>
                <AttachmentList
                  attachments={transaction.attachments}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {showEditModal && transaction && (
        <EditTransaction
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          currentTransaction={transaction}
        />
      )}
    </>
  );
}
